import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Trophy, Medal, Award, Package, Box, Target, RefreshCw, Tv, Users, ScanLine, ClipboardList, MapPin, ChevronDown, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import apiClient from '@/services/apiClient';

/**
 * TV Display Mode for Staff Rankings
 * 
 * Features:
 * - Auto-refresh every 30 seconds
 * - Large, readable fonts for TV display
 * - All 7 tabs from Staff Rankings page
 * - Supports URL params: ?tab=sfg_picker|fg_picker|put_away|sfg_put_away|pieces_processed|fg_pick_slip|fg_packing&shift=all|morning|night
 * - Auto-cycles between tabs (optional)
 * - Optimized for 1920x1080 display
 */
const StaffRankingsTV = () => {
  const [searchParams] = useSearchParams();
  
  const tabParam = searchParams.get('tab') || 'sfg_picker';
  const resolvedTab = tabParam === 'pieces_processed' ? 'pieces_regular' : tabParam;
  const shiftParam = searchParams.get('shift') || 'all';
  const cycleParam = searchParams.get('cycle') === 'true';
  
  const [activeTab, setActiveTab] = useState(resolvedTab);
  const [rankings, setRankings] = useState(null);
  const [piecesRegularData, setPiecesRegularData] = useState([]);
  const [piecesComboData, setPiecesComboData] = useState([]);
  const [packingData, setPackingData] = useState([]);
  const [pickSlipData, setPickSlipData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [expandedStates, setExpandedStates] = useState({});
  const [currentStaffIndex, setCurrentStaffIndex] = useState(0);
  
  // All 8 tabs (pieces split into regular + combo)
  const tabs = ['sfg_put_away', 'sfg_picker', 'pieces_regular', 'pieces_combo', 'put_away', 'fg_pick_slip', 'fg_picker', 'fg_packing'];
  
  const fetchRankings = useCallback(async () => {
    try {
      const response = await apiClient.get(`/wms/staff/rankings?period=daily&shift=${shiftParam}`);
      setRankings(response.data);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Failed to load rankings:', error);
    } finally {
      setLoading(false);
    }
  }, [shiftParam]);
  
  const fetchPiecesProcessed = useCallback(async () => {
    try {
      const [regularRes, comboRes] = await Promise.all([
        apiClient.get(`/sections/dispatch/pieces-processed/all-staff-performance?period=daily&shift=${shiftParam}&processing_type=regular`),
        apiClient.get(`/sections/dispatch/pieces-processed/all-staff-performance?period=daily&shift=${shiftParam}&processing_type=combo`)
      ]);
      setPiecesRegularData(regularRes.data.staff_performance || []);
      setPiecesComboData(comboRes.data.staff_performance || []);
    } catch (error) {
      console.error('Failed to load pieces data:', error);
    }
  }, [shiftParam]);
  
  const fetchPackingPerformance = useCallback(async () => {
    try {
      const response = await apiClient.get(`/wms/packing/staff-performance?period=daily&shift=${shiftParam}`);
      setPackingData(response.data.staff_performance || []);
    } catch (error) {
      console.error('Failed to load packing data:', error);
    }
  }, [shiftParam]);
  
  const fetchPickSlipData = useCallback(async () => {
    try {
      const response = await apiClient.get(`/wms/fg/pick-slips/state-wise-performance?period=daily&shift=${shiftParam}`);
      setPickSlipData(response.data);
    } catch (error) {
      console.error('Failed to load pick slip data:', error);
    }
  }, [shiftParam]);
  
  // Initial fetch and auto-refresh every 30 seconds
  useEffect(() => {
    const fetchAll = () => {
      fetchRankings();
      fetchPiecesProcessed();
      fetchPackingPerformance();
      fetchPickSlipData();
    };
    fetchAll();
    const interval = setInterval(fetchAll, 30000);
    return () => clearInterval(interval);
  }, [fetchRankings, fetchPiecesProcessed, fetchPackingPerformance, fetchPickSlipData]);
  
  // Tab cycling every 15 seconds if enabled
  useEffect(() => {
    if (!cycleParam) return;
    const cycleInterval = setInterval(() => {
      setActiveTab(prev => {
        const currentIndex = tabs.indexOf(prev);
        return tabs[(currentIndex + 1) % tabs.length];
      });
    }, 15000);
    return () => clearInterval(cycleInterval);
  }, [cycleParam]);
  
  // Build the current staff list based on active tab
  const getStaffListForTab = useCallback(() => {
    if (['sfg_picker', 'fg_picker', 'sfg_put_away', 'put_away'].includes(activeTab)) {
      return rankings?.rankings?.[activeTab] || [];
    } else if (activeTab === 'pieces_regular') {
      return piecesRegularData || [];
    } else if (activeTab === 'pieces_combo') {
      return piecesComboData || [];
    } else if (activeTab === 'fg_packing') {
      return packingData || [];
    }
    return [];
  }, [activeTab, rankings, piecesRegularData, piecesComboData, packingData]);

  const staffList = getStaffListForTab();
  const isStaffTab = ['sfg_picker', 'fg_picker', 'sfg_put_away', 'put_away', 'pieces_regular', 'pieces_combo', 'fg_packing'].includes(activeTab);

  // Reset index when tab changes or data changes
  useEffect(() => {
    setCurrentStaffIndex(0);
  }, [activeTab]);

  // Auto-rotate through staff: 60s / total staff, min 3s per staff
  // For PP tabs: auto-switch between Regular and Combo after a full cycle
  useEffect(() => {
    if (!isStaffTab || staffList.length === 0) return;
    const delay = Math.max(3000, Math.floor(60000 / staffList.length));
    const rotateInterval = setInterval(() => {
      setCurrentStaffIndex(prev => {
        const next = prev + 1;
        if (next >= staffList.length) {
          // Full cycle done — if PP tab, switch to the other PP tab
          if (activeTab === 'pieces_regular') {
            setActiveTab('pieces_combo');
          } else if (activeTab === 'pieces_combo') {
            setActiveTab('pieces_regular');
          }
          return 0;
        }
        return next;
      });
    }, delay);
    return () => clearInterval(rotateInterval);
  }, [isStaffTab, staffList.length, activeTab]);

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="w-16 h-16 text-yellow-400" />;
    if (rank === 2) return <Medal className="w-14 h-14 text-gray-300" />;
    if (rank === 3) return <Award className="w-14 h-14 text-amber-600" />;
    return null;
  };
  
  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-blue-400';
    if (score >= 40) return 'text-yellow-400';
    return 'text-red-400';
  };
  
  const categoryConfig = {
    sfg_put_away: { title: 'SFG Put-away', icon: Target, color: 'orange' },
    sfg_picker: { title: 'SFG Pickers', icon: Package, color: 'indigo' },
    pieces_regular: { title: 'PP Regular', icon: ScanLine, color: 'teal' },
    pieces_combo: { title: 'PP Combo', icon: ScanLine, color: 'amber' },
    put_away: { title: 'FG Put-away', icon: Target, color: 'purple' },
    fg_pick_slip: { title: 'FG Pick Slip', icon: ClipboardList, color: 'rose' },
    fg_picker: { title: 'FG Pickers', icon: Box, color: 'emerald' },
    fg_packing: { title: 'FG Packing', icon: Package, color: 'cyan' }
  };
  
  const config = categoryConfig[activeTab];
  const Icon = config?.icon || Package;
  
  // Determine which data shape to use
  const isRankingTab = ['sfg_picker', 'fg_picker', 'sfg_put_away', 'put_away'].includes(activeTab);
  const isPiecesTab = activeTab === 'pieces_regular' || activeTab === 'pieces_combo';
  const isPackingTab = activeTab === 'fg_packing';
  const isPickSlipTab = activeTab === 'fg_pick_slip';
  
  const toggleState = (stateName) => {
    setExpandedStates(prev => ({ ...prev, [stateName]: !prev[stateName] }));
  };
  
  // ========== SINGLE STAFF SPOTLIGHT DISPLAY (used by ranking, pieces, packing tabs) ==========
  const renderStaffSpotlight = () => {
    if (staffList.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-[60vh] text-gray-500">
          <Users className="w-24 h-24 mb-4" />
          <p className="text-2xl">No staff data available</p>
        </div>
      );
    }

    const staff = staffList[currentStaffIndex];
    if (!staff) return null;

    const rank = isRankingTab ? staff.rank : currentStaffIndex + 1;
    const staffName = (activeTab === 'put_away' && staff.display_name) ? staff.display_name : staff.staff_name;
    const volume = (activeTab === 'put_away' || activeTab === 'sfg_put_away') ? staff.total_items
      : isPiecesTab ? staff.total_pieces
      : isPackingTab ? staff.total_pieces
      : staff.total_qty;
    const volumeLabel = (isPiecesTab || isPackingTab) ? 'Pieces' : 'Volume';

    const rankBg = rank === 1 ? 'from-yellow-500/30 to-amber-500/10 border-yellow-500/60'
      : rank === 2 ? 'from-gray-400/25 to-gray-500/10 border-gray-400/50'
      : rank === 3 ? 'from-amber-600/25 to-amber-700/10 border-amber-600/50'
      : 'from-white/10 to-white/5 border-white/20';

    return (
      <div className="flex flex-col items-center justify-center h-[60vh]" data-testid="staff-spotlight">
        {/* Position indicator dots */}
        <div className="flex items-center gap-2 mb-6" data-testid="staff-pagination-dots">
          {staffList.map((_, idx) => (
            <div
              key={idx}
              className={`rounded-full transition-all duration-300 ${
                idx === currentStaffIndex ? 'w-4 h-4 bg-white' : 'w-2 h-2 bg-white/30'
              }`}
            />
          ))}
        </div>

        {/* Horizontal layout: Rank on LEFT + Card on RIGHT */}
        <div className="flex items-center gap-10 w-full max-w-5xl">
          {/* Left: Rank display */}
          <div className="flex flex-col items-center flex-shrink-0 w-48" data-testid="spotlight-rank">
            {getRankIcon(rank)}
            <span className="text-9xl font-black tracking-tight leading-none mt-2" style={{
              color: rank === 1 ? '#facc15' : rank === 2 ? '#d1d5db' : rank === 3 ? '#d97706' : '#9ca3af'
            }}>
              #{rank}
            </span>
            <span className="text-xl text-gray-500 font-medium mt-1">of {staffList.length}</span>
          </div>

          {/* Right: Staff card */}
          <div className={`bg-gradient-to-br ${rankBg} border-2 rounded-3xl p-10 flex-grow text-center transition-all duration-500`}>
            {/* Name */}
            <h2 className="text-6xl font-bold mb-8 tracking-tight" data-testid="spotlight-staff-name">
              {staffName}
            </h2>

            {/* Stats row */}
            <div className={`grid ${(isPiecesTab || isPackingTab) ? 'grid-cols-2' : 'grid-cols-3'} gap-8`}>
              <div>
                <p className="text-5xl font-extrabold text-blue-400" data-testid="spotlight-volume">
                  {(volume || 0).toLocaleString()}
                </p>
                <p className="text-lg text-gray-400 mt-2">{volumeLabel}</p>
              </div>

              {isRankingTab && (
                <>
                  <div>
                    <p className="text-5xl font-extrabold text-purple-400">{staff.speed || 0}</p>
                    <p className="text-lg text-gray-400 mt-2">Items/hr</p>
                  </div>
                  <div>
                    <p className="text-5xl font-extrabold text-green-400">{staff.active_time || 0}</p>
                    <p className="text-lg text-gray-400 mt-2">Active mins</p>
                  </div>
                </>
              )}

              {isPiecesTab && (
                <div>
                  <p className="text-5xl font-extrabold text-cyan-400">{staff.total_bundles || 0}</p>
                  <p className="text-lg text-gray-400 mt-2">Bundles</p>
                </div>
              )}

              {isPackingTab && (
                <div>
                  <p className="text-5xl font-extrabold text-orange-400">{staff.total_boxes || 0}</p>
                  <p className="text-lg text-gray-400 mt-2">Boxes</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Timer bar */}
        <div className="w-full max-w-3xl mt-6">
          <div className="h-1 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-white/40 rounded-full"
              style={{
                animation: `shrink ${Math.max(3, Math.floor(60 / staffList.length))}s linear infinite`
              }}
            />
          </div>
          <p className="text-center text-gray-500 text-sm mt-2">
            {currentStaffIndex + 1} / {staffList.length} &middot; {Math.max(3, Math.floor(60 / staffList.length))}s per staff
          </p>
        </div>

        {/* CSS keyframe for timer bar */}
        <style>{`
          @keyframes shrink {
            from { width: 100%; }
            to { width: 0%; }
          }
        `}</style>
      </div>
    );
  };
  
  // ========== FG PICK SLIP TAB (kept as-is — not staff-based) ==========
  const renderPickSlipTab = () => {
    const summary = pickSlipData?.summary || {};
    const states = pickSlipData?.states || [];
    
    return (
      <>
        <div className="grid grid-cols-5 gap-4 mb-8">
          <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 border border-purple-500/30 rounded-2xl p-4">
            <p className="text-purple-400 text-base mb-1">Opening Balance</p>
            <p className="text-4xl font-bold">{(summary.opening_balance || 0).toLocaleString()}</p>
          </div>
          <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border border-blue-500/30 rounded-2xl p-4">
            <p className="text-blue-400 text-base mb-1">Total Pick Slip Qty</p>
            <p className="text-4xl font-bold">{(summary.total_pick_slip_qty || 0).toLocaleString()}</p>
            <p className="text-blue-400/60 text-sm">{summary.total_slips || 0} slips</p>
          </div>
          <div className="bg-gradient-to-br from-green-500/20 to-green-600/10 border border-green-500/30 rounded-2xl p-4">
            <p className="text-green-400 text-base mb-1">Picked Qty</p>
            <p className="text-4xl font-bold">{(summary.picked_qty || 0).toLocaleString()}</p>
          </div>
          <div className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 border border-amber-500/30 rounded-2xl p-4">
            <p className="text-amber-400 text-base mb-1">In Progress</p>
            <p className="text-4xl font-bold">{(summary.in_progress_qty || 0).toLocaleString()}</p>
          </div>
          <div className="bg-gradient-to-br from-red-500/20 to-red-600/10 border border-red-500/30 rounded-2xl p-4">
            <p className="text-red-400 text-base mb-1">Pending</p>
            <p className="text-4xl font-bold">{(summary.pending_qty || 0).toLocaleString()}</p>
          </div>
        </div>
        
        {states.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-[50vh] text-gray-500">
            <ClipboardList className="w-24 h-24 mb-4" />
            <p className="text-2xl">No pick slip data available</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 max-h-[calc(100vh-420px)] overflow-y-auto pr-2">
            {states.map((stateGroup) => (
              <div
                key={stateGroup.state}
                className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden"
              >
                <button
                  onClick={() => toggleState(stateGroup.state)}
                  className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    {expandedStates[stateGroup.state]
                      ? <ChevronDown className="w-5 h-5 text-gray-400" />
                      : <ChevronRight className="w-5 h-5 text-gray-400" />}
                    <MapPin className="w-5 h-5 text-rose-400" />
                    <span className="text-xl font-bold">{stateGroup.state}</span>
                    <Badge className="bg-white/10 text-gray-300">{stateGroup.customer_count} customers</Badge>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-2xl font-bold">{stateGroup.total_qty.toLocaleString()}</span>
                    {stateGroup.picked_qty > 0 && (
                      <Badge className="bg-green-500/20 text-green-300">{stateGroup.picked_qty.toLocaleString()} picked</Badge>
                    )}
                  </div>
                </button>
                {expandedStates[stateGroup.state] && (
                  <div className="border-t border-white/10">
                    {stateGroup.customers.map((customer, cIdx) => (
                      <div key={cIdx} className="flex items-center justify-between px-6 py-3 border-b border-white/5 last:border-b-0">
                        <div>
                          <p className="text-base font-medium">{customer.customer}</p>
                          {customer.agent && <p className="text-sm text-gray-500">{customer.agent}</p>}
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-lg font-bold">{customer.total_qty.toLocaleString()} qty</span>
                          {customer.picked_qty > 0 && (
                            <Badge className="bg-green-500/20 text-green-300 text-sm">{customer.picked_qty} picked</Badge>
                          )}
                          <Badge className="bg-white/10 text-gray-300 text-sm">{customer.slip_count} slips</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </>
    );
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0f1a] via-[#111827] to-[#1a1f2e] text-white p-6">
      {/* Header */}
      <header className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="bg-gradient-to-br from-yellow-400 to-amber-500 p-3 rounded-2xl">
            <Trophy className="w-10 h-10 text-[#1a1f2e]" />
          </div>
          <div>
            <h1 className="text-4xl font-bold tracking-tight">Staff Leaderboard</h1>
            <p className="text-xl text-gray-400">Today's Performance Rankings</p>
          </div>
        </div>
        
        <div className="flex items-center gap-6">
          {shiftParam !== 'all' && (
            <Badge className={`text-lg py-2 px-4 ${shiftParam === 'morning' ? 'bg-yellow-500/20 text-yellow-300' : 'bg-indigo-500/20 text-indigo-300'}`}>
              {shiftParam === 'morning' ? 'Morning Shift' : 'Night Shift'}
            </Badge>
          )}
          
          {cycleParam && (
            <Badge className="bg-green-500/20 text-green-300 text-lg py-2 px-4 animate-pulse">
              <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
              Auto-cycling
            </Badge>
          )}
          
          <div className="flex items-center gap-2">
            <span className="relative flex h-4 w-4">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-4 w-4 bg-green-500"></span>
            </span>
            <span className="text-green-400 text-lg font-medium">LIVE</span>
          </div>
          
          <div className="text-gray-400 text-lg">
            <Tv className="w-6 h-6 inline mr-2" />
            Updated: {lastRefresh.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
      </header>
      
      {/* Tab Selector — All 8 tabs */}
      <div className="flex justify-center gap-2 mb-8 flex-wrap">
        {tabs.map((tab) => {
          const TabIcon = categoryConfig[tab].icon;
          const isActive = activeTab === tab;
          
          // Get count for each tab
          let count = 0;
          if (['sfg_picker', 'fg_picker', 'sfg_put_away', 'put_away'].includes(tab)) {
            count = (rankings?.rankings?.[tab] || []).length;
          } else if (tab === 'pieces_regular') {
            count = piecesRegularData.length;
          } else if (tab === 'pieces_combo') {
            count = piecesComboData.length;
          } else if (tab === 'fg_packing') {
            count = packingData.length;
          } else if (tab === 'fg_pick_slip') {
            count = pickSlipData?.summary?.total_slips || 0;
          }
          
          return (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex items-center gap-2 px-5 py-3 rounded-xl text-base font-semibold transition-all ${
                isActive 
                  ? 'bg-gradient-to-r from-[#2D5F4F] to-[#3d7a64] text-white shadow-lg shadow-[#2D5F4F]/30' 
                  : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
              data-testid={`tv-tab-${tab}`}
            >
              <TabIcon className="w-5 h-5" />
              {categoryConfig[tab].title}
              <Badge className={`ml-1 ${isActive ? 'bg-white/20' : 'bg-white/10'}`}>
                {count}
              </Badge>
            </button>
          );
        })}
      </div>
      
      {loading ? (
        <div className="flex items-center justify-center h-[50vh]">
          <RefreshCw className="w-16 h-16 animate-spin text-gray-500" />
        </div>
      ) : (
        <>
          {isStaffTab && renderStaffSpotlight()}
          {isPickSlipTab && renderPickSlipTab()}
        </>
      )}
      
      {/* Footer removed - Score field removed per user request */}
    </div>
  );
};

export default StaffRankingsTV;
